import { PORTRAIT_ID } from '../../shared/game/character-build.js';
import { icon, type HudIcon } from './icons.js';

/** One painted portrait from `src/client/assets/portraits`: its id (the file name), its address, and the body its name was made for. */
export interface Portrait { id: string; url: string; ancestry?: string; body?: string; classId?: string }

/**
 * Whether Vite bundled this module (see `audio/combat-sounds.ts`): under the Node test runner `import.meta.env` is absent and there are no
 * portraits.
 */
const bundled = import.meta.env !== undefined;
const files = bundled ? import.meta.glob('../assets/portraits/*.{png,webp}', { eager: true, query: '?url', import: 'default' }) as Record<string, string> : {};

/** A name that starts `<ancestry>-<male|female>-` was painted for that body, and the next word names the class it suggests; any other valid name suits everyone. */
export function portraitFromFile(path: string, url: string): Portrait | null {
  const id = path.split('/').at(-1)!.replace(/\.[a-z]+$/i, '');
  if (!PORTRAIT_ID.test(id)) return null;
  const match = /^([a-z]+)-(male|female)-([a-z]+)/.exec(id);
  return { id, url, ...match ? { ancestry: match[1], body: `${match[1]}-${match[2]}`, classId: match[3] } : {} };
}

export const portraits: Portrait[] = Object.entries(files).flatMap(([path, url]) => portraitFromFile(path, url) ?? []).sort((a, b) => a.id.localeCompare(b.id));
const byId = new Map(portraits.map(portrait => [portrait.id, portrait]));
// The coast's named people (`content/characters/ember-coast-npcs.ts`) have portraits of their own, shown by id but never offered to heroes.
const npcFiles = bundled ? import.meta.glob('../assets/npc/*.{png,webp}', { eager: true, query: '?url', import: 'default' }) as Record<string, string> : {};
// Transformation forms resolve by id too, without appearing in the creator's hero portrait choices.
const formFiles = bundled ? import.meta.glob('../assets/portraits/forms/*.{png,webp}', { eager: true, query: '?url', import: 'default' }) as Record<string, string> : {};
for (const [path, url] of Object.entries({ ...npcFiles, ...formFiles })) { const portrait = portraitFromFile(path, url); if (portrait && !byId.has(portrait.id)) byId.set(portrait.id, { id: portrait.id, url }); }

/** The address of a portrait's picture, or undefined when there is no such file (an empty folder, or a file since removed). */
export const portraitUrl = (id: string | undefined) => id ? byId.get(id)?.url : undefined;

/** Every portrait: those painted for this body first (this class's leading), then for its ancestry, then the ones for everyone, then the rest. */
export function portraitsFor(body: string, ancestry: string, classId?: string, list = portraits): Portrait[] {
  const rank = (portrait: Portrait) => portrait.body === body ? portrait.classId === classId ? 0 : 1 : portrait.ancestry === ancestry ? 2 : portrait.ancestry ? 4 : 3;
  return [...list].sort((a, b) => rank(a) - rank(b) || a.id.localeCompare(b.id));
}

/** A portrait in its bronze frame for the page (party picker, creator, inventory), or the class glyph in its place when there is none. */
export function portraitFrame(id: string | undefined, glyph: HudIcon, className = ''): HTMLElement {
  const frame = document.createElement('span'); frame.className = `portrait-frame ${className}`.trim();
  const url = portraitUrl(id);
  if (url) { const image = document.createElement('img'); image.src = url; image.alt = ''; image.draggable = false; image.decoding = 'async'; frame.append(image); }
  else { frame.classList.add('glyph'); frame.append(icon(glyph)); }
  return frame;
}
